import React, { useState } from 'react';
import { useWorkspace } from '../../context/WorkspaceContext';
import CheckOutlined from '@mui/icons-material/CheckOutlined';

const FREE_FEATURES = [
  '1 workspace',
  'Up to 3 members',
  'Unlimited projects & events',
  'Personnel & company records',
  'Venue database',
];

const PRO_FEATURES = [
  'Unlimited workspaces',
  'Unlimited members',
  'Custom list values',
  'Advanced reporting',
  'Priority support',
  'Stripe invoice generation',
];

export default function BillingSettings() {
  const { activeWorkspace } = useWorkspace();
  const plan = activeWorkspace?.subscription?.plan || 'free';
  const seats = activeWorkspace?.subscription?.seats_purchased || 0;
  const memberCount = activeWorkspace?.members?.length || 0;
  const maxSeats = plan === 'free' ? 3 : seats;

  return (
    <div style={{ maxWidth: '640px', display: 'flex', flexDirection: 'column', gap: '28px' }}>

      {/* Current Plan */}
      <div>
        <SectionLabel>Current Plan</SectionLabel>
        <div style={{
          backgroundColor: '#2a2a2a',
          border: '1px solid #3a3a3a',
          borderRadius: '10px',
          padding: '24px',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: '24px',
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
              <span style={{
                fontFamily: "'Roboto', sans-serif",
                fontWeight: 700,
                fontSize: '20px',
                color: '#FFFFFF',
                textTransform: 'capitalize',
              }}>
                {plan}
              </span>
              <PlanBadge plan={plan} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
              {FREE_FEATURES.map((f) => (
                <FeatureRow key={f} text={f} />
              ))}
            </div>
          </div>
          {plan === 'free' && (
            <div style={{ textAlign: 'right', flexShrink: 0 }}>
              <div style={{
                fontFamily: "'Roboto', sans-serif",
                fontSize: '30px',
                fontWeight: 700,
                color: '#F5F5F5',
              }}>
                $0
              </div>
              <div style={{
                fontFamily: "'Roboto', sans-serif",
                fontSize: '12px',
                color: '#565656',
              }}>
                / month
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Seats */}
      <div>
        <SectionLabel>Seats</SectionLabel>
        <div style={{
          backgroundColor: '#2a2a2a',
          border: '1px solid #3a3a3a',
          borderRadius: '10px',
          padding: '20px 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '24px',
        }}>
          <div>
            <div style={{
              fontFamily: "'Roboto', sans-serif",
              fontWeight: 600,
              fontSize: '15px',
              color: '#F5F5F5',
              marginBottom: '4px',
            }}>
              {memberCount} active {memberCount === 1 ? 'member' : 'members'}
            </div>
            <div style={{
              fontFamily: "'Roboto', sans-serif",
              fontSize: '13px',
              color: '#565656',
            }}>
              {plan === 'free'
                ? `3 seat limit on free plan`
                : `${seats} ${seats === 1 ? 'seat' : 'seats'} purchased`
              }
            </div>
          </div>
          <SeatBar current={memberCount} max={maxSeats} />
        </div>
      </div>

      {/* Upgrade */}
      <div>
        <SectionLabel>Upgrade</SectionLabel>
        <div style={{
          backgroundColor: '#2a2a2a',
          border: '1px solid #3a3a3a',
          borderRadius: '10px',
          padding: '24px',
        }}>
          <div style={{ marginBottom: '16px' }}>
            <div style={{
              fontFamily: "'Roboto', sans-serif",
              fontWeight: 700,
              fontSize: '18px',
              color: '#FFFFFF',
              marginBottom: '4px',
            }}>
              Vance Pro
            </div>
            <div style={{
              fontFamily: "'Roboto', sans-serif",
              fontSize: '13px',
              color: '#565656',
            }}>
              Everything in Free, plus:
            </div>
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '8px',
            marginBottom: '24px',
          }}>
            {PRO_FEATURES.map((f) => (
              <FeatureRow key={f} text={f} accent />
            ))}
          </div>
          <StripeButton />
        </div>
      </div>
    </div>
  );
}

function SectionLabel({ children }) {
  return (
    <div style={{
      fontFamily: "'Roboto', sans-serif",
      fontWeight: 600,
      fontSize: '12px',
      color: '#888888',
      letterSpacing: '1.5px',
      textTransform: 'uppercase',
      marginBottom: '12px',
    }}>
      {children}
    </div>
  );
}

function PlanBadge({ plan }) {
  const map = {
    free:       { bg: 'rgba(86,86,86,0.2)',      text: '#888888' },
    starter:    { bg: 'rgba(33,150,243,0.15)',   text: '#2196F3' },
    pro:        { bg: 'rgba(229,192,75,0.15)',   text: '#E5C04B' },
    enterprise: { bg: 'rgba(57,255,20,0.1)',     text: '#39FF14' },
  };
  const c = map[plan] || map.free;
  return (
    <span style={{
      fontFamily: "'Roboto', sans-serif",
      fontWeight: 600,
      fontSize: '11px',
      color: c.text,
      backgroundColor: c.bg,
      padding: '3px 10px',
      borderRadius: '4px',
      letterSpacing: '0.5px',
      textTransform: 'uppercase',
    }}>
      {plan}
    </span>
  );
}

function FeatureRow({ text, accent }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <CheckOutlined style={{ fontSize: 14, color: accent ? '#39FF14' : '#565656', flexShrink: 0 }} />
      <span style={{
        fontFamily: "'Roboto', sans-serif",
        fontSize: '13px',
        color: '#888888',
      }}>
        {text}
      </span>
    </div>
  );
}

function SeatBar({ current, max }) {
  const pct = max > 0 ? Math.min((current / max) * 100, 100) : 0;
  const color = pct >= 100 ? '#BF2634' : pct >= 80 ? '#E5C04B' : '#39FF14';
  return (
    <div style={{ textAlign: 'right', flexShrink: 0 }}>
      <div style={{
        fontFamily: "'Roboto', sans-serif",
        fontSize: '13px',
        color: '#888888',
        marginBottom: '6px',
      }}>
        {current} / {max}
      </div>
      <div style={{
        width: '100px', height: '6px',
        backgroundColor: '#333333',
        borderRadius: '3px',
        overflow: 'hidden',
      }}>
        <div style={{
          width: `${pct}%`, height: '100%',
          backgroundColor: color,
          borderRadius: '3px',
          transition: 'width 0.3s ease',
        }} />
      </div>
    </div>
  );
}

function StripeButton() {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => console.log('STRIPE_CHECKOUT')}
      style={{
        backgroundColor: hovered ? '#8B1C26' : '#BF2634',
        border: 'none',
        color: '#FFFFFF',
        fontFamily: "'Roboto', sans-serif",
        fontWeight: 500,
        fontSize: '14px',
        padding: '12px 24px',
        borderRadius: '4px',
        cursor: 'pointer',
        transition: 'background-color 0.15s ease',
      }}
    >
      Upgrade to Pro — Powered by Stripe
    </button>
  );
}
